import cardValidator from 'card-validator';
import { useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useL10n } from '@uplift/hooks';
import { CreditCardValidator, MaskedInput, TextInput } from '@uplift/ui-v2';
import { BillingFormType } from './use-billing-form.hook';
import { PaymentsPicker } from './PaymentsPicker.component';
import { useBillingContext } from '../billing.context';


const useStyles = makeStyles()(theme => ({
  creditCards: {
    margin: 0,
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    '& > *': {
      margin: '0 !important'
    }
  },
}));


export type CardDetailsFormProps = {
  isMobile?: boolean;
};


export const CardDetailsForm: React.VFC<CardDetailsFormProps> = ({ isMobile = false }) => {
  const l10n = useL10n();
  const formik = useFormikContext<BillingFormType>();
  const { classes } = useStyles();
  const { balance } = useBillingContext();

  const isAmexCard = useMemo(() => {
    const { card } = cardValidator.number(formik.values.cardNumber);

    if (!card) return false;

    return card.type === 'american-express';
  }, [ formik.values.cardNumber ]);

  const { setFieldValue, setFieldTouched } = formik;
  const resetCvc = useCallback(() => {
    setFieldValue('cvc', '');
    // don't want error showing up for cvc, just empty it
    setFieldTouched('cvc', false);
  }, [ setFieldValue, setFieldTouched ]);

  const cvc = formik.values.cvc;

  // used to strip away cvc if card type is changed and cvc has an invalid length
  useEffect(() => {
    if (cvc?.length === 4 && !isAmexCard) {
      resetCvc();
    }
  }, [ isAmexCard, cvc, resetCvc ]);

  return (
    <Box display="flex" flexDirection="column">
      {balance > 0 && (
        <Box mt={4} px={{ xs: 0, sm: 3 }}>
          <PaymentsPicker name="payments" />
        </Box>
      )}
      <Box mt={4} px={{ xs: 2, sm: 3 }} pb={1}>
        <Typography variant="h4">Payment Method</Typography>
      </Box>
      {isMobile && <Divider />}
      <Box display="flex" flexDirection="column" bgcolor="white" p={{ xs: 2, sm: 3 }}>
        <Box mb={2}>
          <Typography variant="body1">{l10n.accountSettings.billing.paymentCard}</Typography>
        </Box>
        <Box mb={3} maxWidth="400px">
          <TextInput
            fullWidth
            label={l10n.accountSettings.billing.cardholderName}
            name="cardholderName"
            className="fs-exclude"
          />
        </Box>
        <Box display={isMobile ? 'initial' : 'flex'} alignItems="flex-end" gap="16px" mb={3}>
          <Box width="220px">
            <MaskedInput
              fullWidth
              name="cardNumber"
              type="card"
              label={l10n.accountSettings.billing.cardNumber}
              className="fs-exclude"
            />
          </Box>
          <Box mt={1} display="flex" pb={0.5}>
            <CreditCardValidator cardNumber={formik.values.cardNumber} className={classes.creditCards} />
          </Box>
        </Box>
        <Box mb={3} maxWidth="120px">
          <MaskedInput
            fullWidth
            type="cardExpiration"
            name="expirationDate"
            label={l10n.accountSettings.billing.expirationDate}
            helperText={l10n.accountSettings.billing.expirtationDateTip}
            className="fs-exclude"
          />
        </Box>
        <Box maxWidth="120px">
          <MaskedInput
            fullWidth
            type={isAmexCard ? 'cvc' : 'cvc3'}
            name="cvc"
            label={l10n.accountSettings.billing.cvc}
            className="fs-exclude"
          />
        </Box>
      </Box>
    </Box>
  );
};
